import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Briefcase, MapPin, Plus, Send } from "lucide-react";
import VerifiedBadge from "@/components/app/VerifiedBadge";
import { COUNTRIES } from "@/lib/i18n";
import { toast } from "sonner";

export default function Jobs() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [country, setCountry] = useState(user?.country || "FR");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", type: "CDI", city: "", salary: "", description: "", country: user?.country || "FR" });
  const [applyOpen, setApplyOpen] = useState(null);
  const [applyMsg, setApplyMsg] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/jobs", { params: { country } });
      setJobs(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [country]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/jobs", form);
      toast.success("Offre publiée");
      setOpen(false);
      setForm({ title: "", type: "CDI", city: "", salary: "", description: "", country });
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const apply = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/jobs/${applyOpen}/apply`, { message: applyMsg });
      toast.success("Candidature envoyée");
      setApplyOpen(null);
      setApplyMsg("");
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const canPost = ["doctor", "pharmacy"].includes(user?.role);

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-deep">{t("jobs.title")}</h1>
          <p className="text-slate-500 mt-1">Recrutement entre confrères dans le réseau Doctobook.</p>
        </div>
        <div className="flex gap-2">
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="w-40 h-10" data-testid="jobs-country-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {canPost && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="btn-coral" data-testid="post-job-btn"><Plus className="h-4 w-4 me-1" />{t("jobs.post")}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("jobs.post")}</DialogTitle></DialogHeader>
                <form onSubmit={create} className="space-y-3 mt-2">
                  <div><Label>{t("jobs.form.job_title")}</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="job-title-input" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>{t("jobs.form.type")}</Label>
                      <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                        <SelectTrigger data-testid="job-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["CDI", "CDD", "Vacation", "Stage", "Remplacement"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>{t("jobs.form.city")}</Label><Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="job-city-input" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Pays</Label>
                      <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>{t("jobs.form.salary")}</Label><Input value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} placeholder="ex: 120 € / jour" /></div>
                  </div>
                  <div><Label>{t("jobs.form.description")}</Label><Textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="job-desc-input" /></div>
                  <Button type="submit" className="w-full btn-primary" data-testid="job-submit-btn">{t("jobs.post")}</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-300 rounded-xl text-slate-500">
          <Briefcase className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          {t("jobs.no_jobs")}
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map((j) => (
            <article key={j.job_id} className="bg-white border border-slate-200 rounded-xl p-5 card-hover" data-testid={`job-${j.job_id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="font-display text-lg font-bold text-deep">{j.title}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <span className="px-2 py-0.5 bg-slate-100 rounded-full font-semibold">{j.type}</span>
                    <span className="flex items-center gap-1 text-slate-600"><MapPin className="h-3 w-3" />{j.city}</span>
                    {j.salary && <span className="text-[#FF7A5C] font-semibold">{j.salary}</span>}
                    <span className="text-slate-400">· {j.applications_count} {t("jobs.applications")}</span>
                  </div>
                </div>
                {j.posted_by !== user?.user_id && (
                  <Button size="sm" onClick={() => setApplyOpen(j.job_id)} className="btn-coral" data-testid={`apply-${j.job_id}-btn`}>
                    <Send className="h-3.5 w-3.5 me-1" />{t("jobs.apply")}
                  </Button>
                )}
              </div>
              <p className="mt-3 text-sm text-slate-700">{j.description}</p>
              {j.poster && (
                <div className="mt-3 text-xs text-slate-500 flex items-center gap-1">
                  par {j.poster.name} {j.poster.blue_check && <VerifiedBadge size={12} />}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      <Dialog open={!!applyOpen} onOpenChange={(o) => !o && setApplyOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("jobs.apply")}</DialogTitle></DialogHeader>
          <form onSubmit={apply} className="space-y-3 mt-2">
            <Textarea placeholder="Message de motivation…" value={applyMsg} onChange={(e) => setApplyMsg(e.target.value)} rows={5} data-testid="apply-msg-input" />
            <Button type="submit" className="w-full btn-primary" data-testid="apply-submit-btn">Envoyer ma candidature</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
